import { TrendingDown, TrendingUp, Users, Mail, Smartphone } from 'lucide-react';

function formatDelta(delta) {
  if (delta == null || Number.isNaN(delta)) return null;
  if (delta > 0) return { text: `+${delta} pt${delta > 1 ? 's' : ''} vs mois dernier`, positive: true };
  if (delta < 0) return { text: `${delta} pt${Math.abs(delta) > 1 ? 's' : ''} vs mois dernier`, positive: false };
  return { text: 'Stable vs mois dernier', positive: null };
}

function HeroCard({ label, value, sub, accent = 'primary' }) {
  const accents = {
    primary: 'border-primary-100 bg-primary-50/40',
    emerald: 'border-emerald-100 bg-emerald-50/40',
    amber: 'border-amber-100 bg-amber-50/40',
  };
  return (
    <div className={`rounded-xl border p-4 ${accents[accent] || accents.primary}`}>
      <p className="text-sm text-gray-600">{label}</p>
      <p className="mt-1 text-3xl font-semibold text-gray-900">{value}</p>
      {sub && <p className="mt-1 text-xs text-gray-500">{sub}</p>}
    </div>
  );
}

export default function CommunicationStatsPanel({ stats, loading }) {
  if (loading) {
    return (
      <div className="card p-6 text-sm text-gray-500">Chargement des statistiques…</div>
    );
  }

  if (!stats?.summary) {
    return null;
  }

  const { summary, topCampaigns, channels, previousPeriodAvailable } = stats;
  const delta = formatDelta(summary.readRateDeltaPts);
  const best = summary.bestCampaign || topCampaigns?.[0] || null;
  const hasInAppActivity = (summary.inAppSent || 0) > 0;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-medium text-gray-900">Ce mois-ci</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Qui a reçu vos messages, qui les a lus, et quelle campagne a le mieux fonctionné.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <HeroCard
          accent="primary"
          label="Clients contactés"
          value={summary.clientsContacted ?? 0}
          sub="Ont reçu au moins un message (email ou In-App)"
        />
        <HeroCard
          accent="emerald"
          label="Clients ont lu"
          value={summary.clientsRead ?? 0}
          sub="Messages In-App lus dans l’application"
        />
        <HeroCard
          accent="amber"
          label="Meilleure campagne"
          value={best ? `${best.readRatePct ?? '—'} %` : '—'}
          sub={best ? `${best.title} · ${best.reads ?? 0} lecture${(best.reads ?? 0) > 1 ? 's' : ''}` : 'Pas assez de données ce mois-ci'}
        />
      </div>

      {hasInAppActivity && summary.readRatePct != null && (
        <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 px-4 py-3 flex flex-wrap items-center gap-2 text-sm text-emerald-900">
          {delta?.positive === true && <TrendingUp className="h-4 w-4 shrink-0" />}
          {delta?.positive === false && <TrendingDown className="h-4 w-4 shrink-0" />}
          <span>
            Vos clients ont lu <strong>{summary.readRatePct} %</strong> de vos messages In-App ce mois-ci.
            {previousPeriodAvailable && delta?.text && (
              <span className="text-emerald-800"> · {delta.text}</span>
            )}
          </span>
        </div>
      )}

      <p className="text-xs text-gray-500">
        {summary.campaignsSent ?? 0} campagne{(summary.campaignsSent ?? 0) > 1 ? 's' : ''} envoyée
        {(summary.campaignsSent ?? 0) > 1 ? 's' : ''}
        {' · '}
        {summary.emailsDispatched ?? 0} email{(summary.emailsDispatched ?? 0) > 1 ? 's' : ''} expédié
        {(summary.emailsDispatched ?? 0) > 1 ? 's' : ''} sans erreur
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card p-4 border-emerald-100">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <Smartphone className="h-4 w-4 text-emerald-600" />
            In-App · engagement mesuré
          </div>
          {hasInAppActivity ? (
            <>
              <p className="mt-2 text-2xl font-semibold text-gray-900">
                {channels?.inApp?.readRatePct ?? summary.readRatePct ?? 0} %
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {summary.clientsRead ?? 0} client{(summary.clientsRead ?? 0) > 1 ? 's' : ''} ont lu ·{' '}
                {summary.inAppSent ?? 0} message{(summary.inAppSent ?? 0) > 1 ? 's' : ''} envoyé
                {(summary.inAppSent ?? 0) > 1 ? 's' : ''}
              </p>
              {channels?.inApp?.insight === 'CANAL_PRIMAIRE' && (
                <p className="mt-2 text-xs font-medium text-emerald-800">
                  Vos clients réagissent surtout via l’application.
                </p>
              )}
            </>
          ) : (
            <p className="mt-2 text-sm text-gray-500">Aucun message In-App envoyé ce mois-ci.</p>
          )}
        </div>

        <div className="card p-4 border-gray-200">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <Mail className="h-4 w-4 text-gray-500" />
            Email · portée uniquement
          </div>
          <p className="mt-2 text-2xl font-semibold text-gray-900">
            {channels?.email?.dispatched ?? summary.emailsDispatched ?? 0}
          </p>
          <p className="text-sm text-gray-600 mt-1">emails expédiés sans erreur d’envoi</p>
          <p className="mt-2 text-xs text-gray-500">
            {channels?.email?.footnote ||
              'Les statistiques de lecture email arrivent prochainement.'}
          </p>
        </div>
      </div>

      {topCampaigns?.length > 0 && (
        <div className="card p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-3">
            <Users className="h-4 w-4" />
            Campagnes les plus lues
          </div>
          <ul className="space-y-2">
            {topCampaigns.map((c, idx) => (
              <li
                key={c.id}
                className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-gray-50 last:border-0 pb-2 last:pb-0"
              >
                <span className="text-gray-800">
                  <span className="text-gray-400 mr-2">{idx + 1}.</span>
                  {c.title}
                </span>
                <span className="font-medium text-emerald-700">
                  {c.readRatePct} % · {c.reads} lecture{c.reads > 1 ? 's' : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export { formatDelta };
